import React from 'react';
import ReactDOM from 'react-dom';

import {Tracker} from '@udemy/event-tracking';

import {VersionSelector} from './version-selector';
import {CLICK_ORIGIN, DevPortalApiReferenceSelected} from '../../lib/events';

function sendEvent(href: string, origin: CLICK_ORIGIN) {
    const reference = href.split('#')[1];
    Tracker.publishEvent(new DevPortalApiReferenceSelected(reference, origin));
}

interface SpectaQLRendererProps {
    html: string;
}
export const SpectaQLRenderer = ({html}: SpectaQLRendererProps) => {
    const docRef = React.useRef<HTMLDivElement>(null);

    // In this "hacky" useEffect, we remove configuration from the generated spectaql html to fit our needs
    // I removed the udemy logo and the favicon from the generated HTML, but we can do that in the html-loader too
    // Additionally I added `ud-heading-serif-xxl` to the H1 in spectaql/index.html
    React.useEffect(() => {
        // Remove #spectacl from the body tag, we attach it to the div below
        document.body.removeAttribute('id');

        const spectaql = docRef.current;
        if (!spectaql) return;

        const nav = spectaql.querySelector('nav#nav');
        const content = spectaql.querySelector('div#docs');

        // Insert SELECT as the first child of #nav
        let versionSelectorContainer: HTMLDivElement | null = null;
        if (nav) {
            versionSelectorContainer = document.createElement('div');
            ReactDOM.render(<VersionSelector />, versionSelectorContainer);
            nav.insertBefore(versionSelectorContainer, nav.firstChild);
        }

        // This function is called when a user clicks within the spectaql docs at any time
        // We then check the target and send a potential event
        function spectaqlElementClickHandler(e: MouseEvent) {
            const target = e.target as HTMLElement;
            if (target?.tagName === 'A') {
                if (nav?.contains(target)) {
                    const href = (target as HTMLLinkElement).href;
                    sendEvent(href, 'api_reference_sidebar');
                }
            } else if (target?.tagName === 'CODE') {
                if (content?.contains(target)) {
                    // In the content, it's a <code>ID!</code> element wrapped in the <a> tag
                    const href = (target.parentElement as HTMLLinkElement).href;
                    sendEvent(href, 'api_reference_content');
                }
            }
        }
        spectaql.addEventListener('click', spectaqlElementClickHandler, true);

        // Find all the links and rewrite them from #id to /path#id - This could potentially be fixed with <base> tag
        const links = spectaql.querySelectorAll('a');
        if (links) {
            links.forEach((anchorNode) => {
                const href = anchorNode.href;
                const [domain, id] = href.split('#');
                if (!domain.includes('/dev-portal')) {
                    // Anything that is not dev portal should open in a new tab
                    anchorNode.target = '_blank';
                } else {
                    // Set correct hash so browser jumps to the correct section
                    const path = window.location.pathname;
                    anchorNode.href = `${path}#${id}`;
                }
            });
        }

        const headings = spectaql.querySelectorAll('.doc-heading');
        if (headings) {
            headings.forEach((headingNode) => {
                headingNode.classList.add('ud-heading-serif-xxl');
                headingNode.classList.remove('doc-heading');
            });
        }

        const smallHeadings = [
            ...(spectaql.querySelectorAll('.definition-heading') || []),
            ...(spectaql.querySelectorAll('.operation-heading ') || []),
        ];

        if (smallHeadings.length) {
            smallHeadings.forEach((headingNode) => {
                headingNode.classList.add('ud-heading-serif-xl');
            });
        }

        return () => {
            spectaql.removeEventListener('click', spectaqlElementClickHandler, true);
            if (versionSelectorContainer) {
                ReactDOM.unmountComponentAtNode(versionSelectorContainer);
            }
        };
    }, []);

    return <div id="spectaql" dangerouslySetInnerHTML={{__html: html}} ref={docRef} />;
};
